import { useState } from "react";
import { Mail, Phone, MapPin, Linkedin, Instagram, Facebook, ArrowUpRight, CheckCircle2 } from "lucide-react";
import Reveal from "@/components/Reveal";
import SectionLabel from "@/components/SectionLabel";
import { CONTACT } from "@/data/site";
import SEO from "@/components/SEO";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(form.subject || `New enquiry from ${form.name}`);
    const body = encodeURIComponent(
      `Name: ${form.name}\nEmail: ${form.email}\n\n${form.message}`
    );
    // Open the user's default mail client with the message pre-filled
    window.location.href = `mailto:${CONTACT.email}?subject=${subject}&body=${body}`;
    setSent(true);
  };

  const contactSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact AmagicTech Solutions",
    "url": "https://amagictechsolutions.com/contact",
    "description": "Get in touch with AmagicTech Solutions. Talk directly with engineers and product managers in Coimbatore, India or Muscat, Oman.",
    "mainEntity": {
      "@type": "Organization",
      "name": "AmagicTech Solutions",
      "email": "info@amagictechsolutions.com",
      "telephone": "+91-98432-44699",
      "address": [
        {
          "@type": "PostalAddress",
          "streetAddress": "Civil Aerodrome Post, SITRA",
          "addressLocality": "Coimbatore",
          "addressRegion": "Tamil Nadu",
          "postalCode": "641014",
          "addressCountry": "IN"
        },
        {
          "@type": "PostalAddress",
          "streetAddress": "Al Khuwair, Way 3514",
          "addressLocality": "Muscat",
          "addressRegion": "Muscat Governorate",
          "addressCountry": "OM"
        }
      ]
    }
  };

  return (
    <main data-testid="contact-page" className="pt-20">
      <SEO 
        title="Contact Us"
        description="Get in touch with AmagicTech Solutions. Reach out to our engineering teams in Coimbatore, India or Muscat, Oman to discuss your web, mobile, or AI project."
        url="/contact"
        keywords="contact software company, hire software engineers, Coimbatore software company, Muscat IT solutions, web development enquiry"
        schema={contactSchema}
      />
      <section className="container-tight pb-20">
        <Reveal>
          <SectionLabel testid="contact-label">Contact</SectionLabel>
        </Reveal>
        <Reveal delay={0.05}>
          <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter text-zinc-900 leading-[0.98]">
            Let&apos;s build
            <br /><span className="text-zinc-400">something real.</span>
          </h1>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-8 max-w-2xl text-zinc-600 text-lg leading-relaxed">
            Tell us a bit about your project. We typically reply within one business day and
            can align with your time zone, tools, and compliance standards.
          </p>
        </Reveal>
      </section>

      <section className="container-tight mt-16 grid md:grid-cols-12 gap-10 pb-24">
        {/* FORM */}
        <div className="md:col-span-7">
          <Reveal>
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="border border-zinc-200 p-7 md:p-10 bg-white"
            >
              <p className="label-mono">Send us a message</p>
              <h2 className="mt-4 font-display text-2xl md:text-3xl text-zinc-900 tracking-tight">
                We respect your privacy and will never share your information.
              </h2>

              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Your name *" name="name" value={form.name} onChange={onChange} testid="contact-input-name" required />
                <Field label="Email address *" name="email" type="email" value={form.email} onChange={onChange} testid="contact-input-email" required />
              </div>
              <div className="mt-5">
                <Field label="Subject" name="subject" value={form.subject} onChange={onChange} testid="contact-input-subject" />
              </div>
              <div className="mt-5">
                <label className="label-mono block mb-2">Your message *</label>
                <textarea
                  name="message"
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={onChange}
                  required
                  rows={6}
                  placeholder="Tell us about your project, timeline, and goals…"
                  className="w-full bg-white border border-zinc-300 text-zinc-900 placeholder-zinc-400 px-4 py-3 focus:border-zinc-500 transition-colors text-[15px] leading-relaxed resize-none"
                />
              </div>

              <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <p className="text-zinc-500 text-xs font-mono-tech">
                  Opens your email · {CONTACT.email}
                </p>
                <button
                  type="submit"
                  data-testid="contact-submit"
                  className="inline-flex items-center justify-center gap-2 bg-zinc-900 text-white px-6 py-3 text-sm font-medium hover:bg-zinc-700 transition-colors"
                >
                  Send message <ArrowUpRight className="w-4 h-4" />
                </button>
              </div>

              {sent && (
                <div
                  data-testid="contact-success"
                  className="mt-6 border border-zinc-200 bg-zinc-50 p-4 flex items-start gap-3"
                >
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                  <div>
                    <p className="text-zinc-900 text-sm font-medium">Email client opened.</p>
                    <p className="text-zinc-600 text-sm mt-1">
                      If nothing happened, simply email us directly at{" "}
                      <a className="underline" href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a>.
                    </p>
                  </div>
                </div>
              )}
            </form>
          </Reveal>
        </div>

        {/* SIDE INFO */}
        <aside className="md:col-span-5 space-y-px">
          <Reveal>
            <div className="border border-zinc-200 p-7 bg-white">
              <p className="label-mono">Email</p>
              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="contact-info-email"
                className="mt-3 inline-flex items-center gap-2 text-zinc-900 text-lg font-display tracking-tight hover:opacity-80"
              >
                <Mail className="w-4 h-4 text-zinc-600" /> {CONTACT.email}
              </a>
            </div>
          </Reveal>

          {CONTACT.offices.map((o, idx) => (
            <Reveal key={o.city} delay={0.05 * (idx + 1)}>
              <div className="border-x border-b border-zinc-200 p-7 bg-white">
                <p className="label-mono flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5" /> {o.city}
                </p>
                <p className="mt-3 text-zinc-700 text-[15px] whitespace-pre-line leading-relaxed">{o.address}</p>
                <p className="mt-3 font-mono-tech text-sm text-zinc-600 flex items-center gap-2">
                  <Phone className="w-3.5 h-3.5" /> {o.phone}
                </p>
              </div>
            </Reveal>
          ))}

          <Reveal delay={0.2}>
            <div className="border-x border-b border-zinc-200 p-7 bg-white">
              <p className="label-mono">Office hours</p>
              <ul className="mt-3 space-y-2 text-zinc-700 text-[15px]">
                <li><span className="text-zinc-500 font-mono-tech text-xs mr-3">MON–FRI</span>{CONTACT.hours.weekdays.replace("Mon – Fri · ", "")}</li>
                <li><span className="text-zinc-500 font-mono-tech text-xs mr-3">SAT&nbsp;&nbsp;&nbsp;&nbsp;</span>{CONTACT.hours.saturday}</li>
                <li><span className="text-zinc-500 font-mono-tech text-xs mr-3">SUN&nbsp;&nbsp;&nbsp;&nbsp;</span>{CONTACT.hours.sunday}</li>
              </ul>
              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  href={CONTACT.linkedin}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="contact-linkedin"
                  aria-label="LinkedIn"
                  className="inline-flex items-center justify-center w-10 h-10 border border-zinc-300 text-zinc-700 hover:text-zinc-900 hover:border-zinc-400 hover:bg-zinc-50 transition-colors"
                >
                  <Linkedin className="w-4 h-4" />
                </a>
                <a
                  href={CONTACT.instagram}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="contact-instagram"
                  aria-label="Instagram"
                  className="inline-flex items-center justify-center w-10 h-10 border border-zinc-300 text-zinc-700 hover:text-zinc-900 hover:border-zinc-400 hover:bg-zinc-50 transition-colors"
                >
                  <Instagram className="w-4 h-4" />
                </a>
                <a
                  href={CONTACT.facebook}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="contact-facebook"
                  aria-label="Facebook"
                  className="inline-flex items-center justify-center w-10 h-10 border border-zinc-300 text-zinc-700 hover:text-zinc-900 hover:border-zinc-400 hover:bg-zinc-50 transition-colors"
                >
                  <Facebook className="w-4 h-4" />
                </a>
              </div>
            </div>
          </Reveal>
        </aside>
      </section>
    </main>
  );
}

function Field({ label, name, type = "text", value, onChange, testid, required }) {
  return (
    <div>
      <label className="label-mono block mb-2">{label}</label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="w-full bg-white border border-zinc-300 text-zinc-900 px-4 py-3 focus:border-zinc-500 transition-colors text-[15px]"
      />
    </div>
  );
}
